import React from "react";
import ReactPlayer from "react-player";

const BRAND = { navy: "#062b6c", green: "#00ad0f" };

const Certification = () => {
  return (
    <div className="relative min-h-screen bg-[#020617] text-slate-50">
      {/* Futuristic background layers */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.12] [background-image:linear-gradient(to_right,#0b1220_1px,transparent_1px),linear-gradient(to_bottom,#0b1220_1px,transparent_1px)] [background-size:46px_46px]" />
      <div className="pointer-events-none absolute -left-32 top-10 h-72 w-72 rounded-full bg-[#00ad0f]/25 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-72 w-72 rounded-full bg-[#2563eb]/30 blur-3xl" />

      {/* HERO */}
      <section className="relative pt-28 md:pt-32 lg:pt-36 border-b border-white/10 overflow-hidden">
        <div className="relative mx-auto flex max-w-6xl flex-col px-5 pb-10 md:pb-14">
          {/* meta row */}
          <div className="mb-5 flex items-center justify-between text-[11px] uppercase tracking-[0.28em] text-slate-400">
            <span className="flex items-center gap-2">
              <span
                className="h-2 w-2 rounded-full shadow-[0_0_12px_rgba(34,197,94,0.8)]"
                style={{ background: BRAND.green }}
              />
              Scaftech Nigeria • Compliance & Recognition
            </span>
            <span className="hidden text-[10px] text-slate-500 sm:inline">
              ISO 9001:2015 • Global Standards
            </span>
          </div>

          <div className="grid gap-8 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1.1fr)] md:items-center">
            {/* left: text */}
            <div className="relative rounded-3xl border border-white/10 bg-[#020819]/90 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.7)] backdrop-blur-xl md:p-8">
              {/* accent */}
              <div className="absolute inset-x-8 top-0 h-[3px] rounded-b-full bg-gradient-to-r from-[#00ad0f] via-white/40 to-[#062b6c]" />

              <p className="mb-2 text-[11px] uppercase tracking-[0.32em] text-slate-400">
                Awards &amp; Certifications
              </p>
              <h1 className="mb-3 text-3xl font-semibold uppercase text-slate-50 md:text-4xl lg:text-[2.6rem] lg:leading-tight">
                <span className="inline-block bg-gradient-to-r from-[#00ad0f] to-[#7fffb0] bg-clip-text text-transparent">
                  Trust Built on Global Standards
                </span>
              </h1>
              <p className="max-w-xl text-sm leading-relaxed text-slate-200 md:text-[0.98rem]">
                Scaftech Engineering Services operates under a robust quality
                management system and holds multiple accreditations and
                memberships with leading regulatory bodies and professional
                organizations—reinforcing our commitment to safety, integrity,
                and technical excellence.
              </p>

              <div className="mt-5 grid gap-3 text-[11px] md:grid-cols-3">
                <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-3">
                  <p className="uppercase tracking-[0.22em] text-emerald-300">
                    Quality Assured
                  </p>
                  <p className="mt-1 text-xs text-emerald-100">
                    ISO-aligned systems, audited and continuously improved.
                  </p>
                </div>
                <div className="rounded-2xl border border-sky-400/30 bg-sky-400/5 px-3 py-3">
                  <p className="uppercase tracking-[0.22em] text-sky-300">
                    Global Bodies
                  </p>
                  <p className="mt-1 text-xs text-sky-100">
                    Membership across drilling, lifting, safety, and labour.
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-500/40 bg-slate-800/60 px-3 py-3">
                  <p className="uppercase tracking-[0.22em] text-slate-300">
                    Compliance First
                  </p>
                  <p className="mt-1 text-xs text-slate-200">
                    Aligned with local and international regulatory frameworks.
                  </p>
                </div>
              </div>
            </div>

            {/* right: hero image */}
            <div className="relative h-56 overflow-hidden rounded-3xl border border-white/10 bg-slate-900/70 shadow-[0_24px_80px_rgba(0,0,0,0.8)] md:h-[320px]">
              <img
                src="/new_img/rooftop-fall.png"
                alt="Rooftop fall protection inspection"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="pointer-events-none absolute inset-0 opacity-40 [background-image:linear-gradient(to_right,rgba(15,23,42,0.9)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.9)_1px,transparent_1px)] [background-size:32px_32px]" />

              <div className="absolute bottom-4 left-5 right-5 flex items-center justify-between text-[11px] text-slate-100">
                <div className="max-w-xs truncate">
                  <span className="block text-[10px] uppercase tracking-[0.26em] text-slate-400">
                    Fall Protection • Asset Safety
                  </span>
                  <span className="truncate text-sm">
                    Certified rooftop fall protection solutions
                  </span>
                </div>
                <span className="text-[9px] text-slate-300">
                  Field-Validated • Compliant
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ACCREDITATION LIST + VIDEO */}
      <section className="relative py-10 md:py-16">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-5 md:flex-row md:px-5">
          {/* text list */}
          <div className="flex w-full flex-col gap-4 rounded-3xl border border-slate-700/60 bg-slate-900/70 p-6 text-sm text-slate-200 shadow-[0_18px_60px_rgba(0,0,0,0.7)] backdrop-blur-xl md:p-8">
            <h3 className="text-lg font-bold uppercase md:text-xl">
              Accreditation &amp; Professional Memberships
            </h3>
            <p className="text-[0.95rem] text-slate-300">
              In addition to our quality management system, Scaftech Engineering
              Services has accreditation and membership with relevant government
              agencies, local and international professional bodies such as:
            </p>

            <ul className="mt-2 flex list-disc flex-col gap-2 pl-5 text-[0.95rem]">
              <li>International Association of Drilling Contractors (IADC)</li>
              <li>American Bureau of Shipping (ABS)</li>
              <li>Lifting Equipment Engineers Association (LEEA)</li>
              <li>Final Investment Decision (FID)</li>
              <li>Federal Ministry of Labour (FML)</li>
              <li>Occupational Safety and Health Administration (OSHA)</li>
              <li>Department of Petroleum Resources (DPR)</li>
              <li>FALLPROTEC</li>
              <li>Institute of Safety Professionals of Nigeria (ISPON)</li>
            </ul>
          </div>

          {/* video */}
          <div className="w-full rounded-3xl border border-slate-700/60 bg-black/60 p-3 shadow-[0_18px_60px_rgba(0,0,0,0.8)]">
            <div className="aspect-video overflow-hidden rounded-2xl">
              <ReactPlayer
                url="https://www.youtube.com/watch?v=E3odTnNeSA8"
                width="100%"
                height="100%"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ACCREDITATION IMAGE STRIP */}
      <section className="relative pb-14">
        <div className="flex flex-col items-center gap-8 px-5 pt-4 md:px-10">
          <div className="w-full text-center text-xl font-semibold leading-[1] text-slate-50 md:text-2xl">
            ACCREDITATION &amp; PROFESSIONAL
            <span className="why_choose_new_section_area_1_title_span">
              &nbsp; MEMBERSHIPS
            </span>
          </div>

          <div className="mx-auto w-full max-w-3xl rounded-3xl border border-slate-700/60 bg-slate-900/60 p-4 shadow-[0_18px_60px_rgba(0,0,0,0.7)]">
            <img
              src="/new_img/certification.png"
              alt="Scaftech accreditation and professional memberships"
              className="mx-auto h-auto w-full max-w-full object-contain"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Certification;
